/**
 * A save that loses - to a conflict, or to a code typed in from another device - is kept, not
 * destroyed: `nightdrop.backup.<time>` holds it as JSON. localStorage is small and the game's own
 * saves come first, so only the newest few are kept and a backup that does not fit is cut down
 * to the profile (the part that cannot be replayed) before it is given up on.
 */

import type { CloudKey, CloudState, KeyValueStore } from './types';
import { BACKUP_PREFIX, CLOUD_KEY } from './types';

export const MAX_BACKUPS = 2;

export interface BackupRecord {
  at: number;
  why: 'conflict' | 'use-code';
  /** Whose values these are: this device's, or the server's copy that this device overrode. */
  side: 'local' | 'server';
  /** `use-code` only: the code this device was using, so the old cloud save can still be reached. */
  previousCode?: string;
  keys: Partial<Record<CloudKey, string>>;
}

function drop(storage: KeyValueStore, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // A store that will not delete will not have room either; the write below reports that.
  }
}

/**
 * Writes the backup and records its key in `state.backups`. Returns the key, or null when there
 * was nothing worth keeping or no room for even the profile.
 */
export function keepBackup(storage: KeyValueStore, state: CloudState, record: BackupRecord): string | null {
  const kept = Object.entries(record.keys).filter(([, value]) => value !== undefined && value !== '');
  if (kept.length === 0) return null;
  while (state.backups.length >= MAX_BACKUPS) {
    const oldest = state.backups.shift();
    if (oldest !== undefined) drop(storage, oldest);
  }
  let key = `${BACKUP_PREFIX}${record.at}`;
  // Two backups in the same millisecond (a conflict inside a code change) must not share a key.
  while (state.backups.includes(key) || storage.getItem(key) !== null) key += 'x';

  const write = (attempt: BackupRecord): boolean => {
    try {
      storage.setItem(key, JSON.stringify(attempt));
      return true;
    } catch {
      return false;
    }
  };
  const full: BackupRecord = { ...record, keys: Object.fromEntries(kept) };
  let written = write(full);
  if (!written && state.backups.length > 0) {
    // Over quota: the older backups go first.
    for (const old of state.backups.splice(0)) drop(storage, old);
    written = write(full);
  }
  const profile = record.keys[CLOUD_KEY.profile];
  if (!written && profile !== undefined && profile !== '' && kept.length > 1) {
    written = write({ ...record, keys: { [CLOUD_KEY.profile]: profile } });
  }
  if (!written) return null;
  state.backups.push(key);
  return key;
}
